import { Controller } from '@hotwired/stimulus'

/**
 * Gates the survey "Next"/"Submit" button on a valid answer for the current
 * question: it stays disabled until the parent makes a selection (or types /
 * picks an answer), replacing the previous submit-then-show-error flow.
 * Optional questions leave the button enabled.
 *
 * The button lives in the fixed footer — outside the <form> — and is wired to
 * it via `form=`, so it's resolved by that association rather than as a target.
 */
export default class extends Controller {
  static values = { optional: Boolean }
  declare readonly optionalValue: boolean

  private submitButton: HTMLButtonElement | null = null

  connect() {
    const form = this.element as HTMLFormElement
    this.submitButton = document.querySelector<HTMLButtonElement>(
      `button[form="${form.id}"]`,
    )
    this.element.addEventListener('input', this.refresh)
    this.element.addEventListener('change', this.enforceExclusivity)
    this.element.addEventListener('change', this.refresh)
    this.refresh()
  }

  disconnect() {
    this.element.removeEventListener('input', this.refresh)
    this.element.removeEventListener('change', this.enforceExclusivity)
    this.element.removeEventListener('change', this.refresh)
  }

  // Keeps a mutually-exclusive checkbox (e.g. "None of the above", marked with
  // `data-survey-answer-exclusive`) from coexisting with the others: checking it
  // clears the rest, and checking any other option clears it.
  private enforceExclusivity = (event: Event) => {
    const target = event.target
    if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') {
      return
    }
    if (!target.checked) return

    const targetIsExclusive = target.hasAttribute(
      'data-survey-answer-exclusive',
    )
    const checkboxes = this.element.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][name="answer"]',
    )
    checkboxes.forEach((checkbox) => {
      if (checkbox === target) return
      const checkboxIsExclusive = checkbox.hasAttribute(
        'data-survey-answer-exclusive',
      )
      if (targetIsExclusive || checkboxIsExclusive) {
        checkbox.checked = false
      }
    })
  }

  private refresh = () => {
    if (this.submitButton) this.submitButton.disabled = !this.hasAnswer()
  }

  private hasAnswer(): boolean {
    if (this.optionalValue) return true
    const fields = this.element.querySelectorAll<
      HTMLInputElement | HTMLTextAreaElement
    >('[name="answer"]')
    return Array.from(fields).some((field) => {
      if (
        field instanceof HTMLInputElement &&
        (field.type === 'radio' || field.type === 'checkbox')
      ) {
        return field.checked
      }
      return field.value.trim() !== ''
    })
  }
}
